import { useEffect, useState } from "react";

import { useAuthSession } from "@/lib/session-context";
import { GAS_UNITS, type GasUnit } from "@/types/gas-units";

const KEY_PREFIX = "app.gas-unit";
const EVENT_NAME = "app:gas-unit";

const keyFor = (userId: string) => `${KEY_PREFIX}:${userId}`;

/**
 * The signed-in user's natural gas display unit (AB#8545) — Ccf, Mcf, Dth,
 * therms or MMBtu. Persisted to `localStorage` and fanned out the same way
 * the electric pricing unit is, so a pick takes effect with no Save button.
 * The key carries the user id, so the choice is the viewing user's own on a
 * shared browser. `null` means the user never chose, and every gas surface
 * keeps its own unit.
 *
 * `userId` is optional because the shell reads this before its session
 * resolves; with none, the hook reports `null`.
 */
export function useGasUnit(
  userId: string | undefined,
): [GasUnit | null, (unit: GasUnit) => void] {
  const [unit, setUnit] = useState<GasUnit | null>(() => readStored(userId));

  // Re-read when the signed-in user changes (or first resolves).
  const [loadedFor, setLoadedFor] = useState(userId);
  if (loadedFor !== userId) {
    setLoadedFor(userId);
    setUnit(readStored(userId));
  }

  useEffect(() => {
    if (!userId) return;
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<{ userId: string; unit: GasUnit }>).detail;
      if (detail.userId === userId) setUnit(detail.unit);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === keyFor(userId)) setUnit(parse(e.newValue));
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, [userId]);

  const update = (next: GasUnit) => {
    if (!userId) return;
    setUnit(next);
    try {
      localStorage.setItem(keyFor(userId), next);
    } catch {
      // localStorage may throw in private mode / disabled storage — the
      // in-memory state still works for the current page.
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { userId, unit: next } }));
  };

  return [unit, update];
}

/** The viewing user's gas unit, for a component inside the shell whose
 *  output depends on it — subscribing re-renders it when the pick changes. */
export function useEffectiveGasUnit(): GasUnit | null {
  const session = useAuthSession();
  const [unit] = useGasUnit(session.user_id);
  return unit;
}

function readStored(userId: string | undefined): GasUnit | null {
  if (!userId) return null;
  try {
    return parse(localStorage.getItem(keyFor(userId)));
  } catch {
    return null;
  }
}

/** Anything not in the catalog reads as "never chose". */
function parse(raw: string | null): GasUnit | null {
  return GAS_UNITS.includes(raw as GasUnit) ? (raw as GasUnit) : null;
}
